/**
 * Pure, reusable transforms for role menu arrays.
 *
 * These let a role's menu be cloned from another role's menu and then diverge,
 * without ever mutating the source menu (so editing one role can never affect
 * the role it was cloned from). Menu `component` values are React elements that
 * are only ever replaced, never mutated, so carrying them by reference is safe.
 */

/**
 * Return a fresh copy of a menu array. Each item is shallow-cloned, including its
 * nested `collapse` children, so overrides applied to the clone don't leak back
 * into the original menu.
 */
export const cloneMenu = (menu = []) =>
  menu.map((item) => ({
    ...item,
    ...(item.collapse
      ? { collapse: item.collapse.map((child) => ({ ...child })) }
      : {}),
  }))

/** Return a new menu with the item matching `key` patched (shallow-merged). */
export const overrideByKey = (menu = [], key, patch = {}) =>
  menu.map((item) => (item.key === key ? { ...item, ...patch } : item))

/** Return a new menu without the item matching `key`. */
export const removeByKey = (menu = [], key) =>
  menu.filter((item) => item.key !== key)

/** Return a new menu with extra items appended. */
export const addItems = (menu = [], ...items) => [...menu, ...items]

/**
 * Drop the "leave-request" item for non-managers. Mirrors the original inline
 * behaviour in App.js for LXO/LX roles.
 */
export const filterLeaveRequestForNonManager = (menu = [], { isManager } = {}) =>
  isManager ? menu : removeByKey(menu, "leave-request")
